// SENTINEL C2 Edge Intelligence Platform — Type Definitions

// ===== Operational Domains =====
export type Domain = 'AIR' | 'SPACE' | 'CYBER' | 'GROUND' | 'SEA'

export const DOMAIN_CONFIG: Record<Domain, { label: string; color: string }> = {
  AIR: { label: 'Air', color: '#3b82f6' },
  SPACE: { label: 'Space', color: '#8b5cf6' },
  CYBER: { label: 'Cyber', color: '#06b6d4' },
  GROUND: { label: 'Ground', color: '#f59e0b' },
  SEA: { label: 'Sea', color: '#0ea5e9' },
}

// ===== Operational Tempos =====
export type Tempo =
  | 'STEADY_STATE'
  | 'SURGE'
  | 'ATTRITION'
  | 'POSTURE_SHIFT'
  | 'RECONSTITUTION'
  | 'DISTRIBUTED_OPS'

export const TEMPO_CONFIG: Record<Tempo, { label: string; color: string; cssClass: string }> = {
  STEADY_STATE: { label: 'Steady State', color: '#38bdf8', cssClass: 'tempo-steady' },
  SURGE: { label: 'Surge', color: '#fbbf24', cssClass: 'tempo-surge' },
  ATTRITION: { label: 'Attrition', color: '#f87171', cssClass: 'tempo-attrition' },
  POSTURE_SHIFT: { label: 'Posture Shift', color: '#a78bfa', cssClass: 'tempo-posture' },
  RECONSTITUTION: { label: 'Reconstitution', color: '#34d399', cssClass: 'tempo-recon' },
  DISTRIBUTED_OPS: { label: 'Distributed Ops', color: '#22d3ee', cssClass: 'tempo-distributed' },
}

// ===== Threat Levels =====
export type ThreatLevel = 'GREEN' | 'AMBER' | 'RED' | 'BLACK'

export const THREAT_CONFIG: Record<ThreatLevel, { label: string; color: string; cssClass: string }> = {
  GREEN: { label: 'Green', color: '#22c55e', cssClass: 'threat-green' },
  AMBER: { label: 'Amber', color: '#f59e0b', cssClass: 'threat-amber' },
  RED: { label: 'Red', color: '#ef4444', cssClass: 'threat-red' },
  BLACK: { label: 'Black', color: '#6b7280', cssClass: 'threat-black' },
}

// ===== 4-Node Fingerprint =====
export interface AssetFingerprint {
  readiness: number  // 0-100
  platform: number   // 0-100
  logistics: number  // 0-100
  mission: number    // 0-100
}

// ===== Force Composition =====
export interface AssetSummary {
  id: string
  asset_type: string
  domain: string
  role: string
  assigned_count: number
  mc_rate: number
  mc_rate_goal: number
  mc_rate_trend: string
  readiness_score: number
  threat_exposure: number
  current_tempo: string
  fingerprint: AssetFingerprint | null
}

export interface ForceCompositionResponse {
  assets: AssetSummary[]
  total_assets: number
  fleet_wide_mc_rate: number
  domain_breakdown: Record<string, number>
}

// ===== Asset Detail =====
export interface AssetDetail {
  id: string
  asset_type: string
  domain: string
  role: string
  assigned_count: number
  real_fleet_size: number
  mc_rate: number
  mc_rate_goal: number
  mc_rate_trend: string
  mc_rates_historical: Record<string, number>
  avg_aircraft_age: number
  readiness_score: number
  threat_exposure: number
  current_tempo: string
  fingerprint: AssetFingerprint
  operational_metrics: {
    assets_available: number
    assets_in_maintenance: number
    sorties_per_period: number
    mission_effectiveness: number
    crew_per_aircraft: number
    turnaround_min: number
    o_and_m_per_fh: number
  }
  sustainment_challenges: string[]
}

// ===== Tempo Alerts =====
export interface TempoAlert {
  id: string
  timestamp: string
  asset_type: string
  asset_id: string
  previous_tempo: string
  new_tempo: string
  confidence: number
  drivers: string[]
  threat_level: string
  tempo_trail: string[]
}

// ===== Fusion Clusters =====
export interface FusionEdgePair {
  from: string
  from_role: string
  to: string
  to_role: string
  r: number
}

export interface FusionCluster {
  id: string
  name: string
  domains: string[]
  assets: string[]
  correlation_strength: number
  pattern_type: string
  description: string
  mc_rates?: Record<string, number>
  edge_pairs?: FusionEdgePair[]
}

// ===== AAR =====
export interface AARCase {
  id: string
  title: string
  scenario: string
  date: string
  sentinel_classification: string
  actual_outcome: string
  accuracy_pct: number
  key_findings: string[]
  lessons_learned: string
}

export interface AARScorecard {
  total_cases: number
  correct_classifications: number
  accuracy: number
  avg_lead_time_hours: number
  false_positive_rate: number
  false_negative_rate: number
  by_tempo: Record<string, { cases: number; accuracy: number }>
}

// ===== AAR Refueling Simulation =====
export type ReceiverPriority = 'HIGH' | 'MEDIUM' | 'LOW'

export interface AARReceiver {
  id: string
  callsign: string
  assetType: string
  fuelCapacity: number
  currentFuel: number
  burnRate: number
  bingoFuel: number
  jokerFuel: number
  requestedOffload: number
  flightSize: number  // 1-4, multiplier for fuel calcs
  priority: ReceiverPriority
  missionEssential: boolean
  arctWindow: [number, number]
  status: 'WAITING' | 'IN_QUEUE' | 'ON_BOOM' | 'COMPLETE' | 'DIVERTED' | 'EMERGENCY'
}

export interface AARTanker {
  id: string
  callsign: string
  assetType: string
  totalOffload: number
  offloaded: number
  flowRate: number
  arTrack: string
  availableWindow: [number, number]
  status: 'AVAILABLE' | 'REFUELING' | 'RTB' | 'CANCELLED'
  queuedReceivers: string[]
  currentReceiver: string | null
}

export interface ARTrack {
  id: string
  name: string
  altitude: string
  tankerIds: string[]
}

export interface AARScenarioConfig {
  name: string
  timeWindowMin: number
  startTimeZulu: string  // HHMM format, e.g. "0000"
  tankers: AARTanker[]
  receivers: AARReceiver[]
  arTracks: ARTrack[]
}

export interface VoiceCommEntry {
  simTime: number
  from: string
  to: string
  message: string
  type: 'CHECK_IN' | 'CLEARED' | 'COMPLETE' | 'EMERGENCY' | 'CONTINGENCY' | 'INFO'
}

export interface AARContingency {
  id: string
  type: 'TANKER_CANCEL' | 'RECEIVER_EMERGENCY' | 'FUEL_SHORTFALL' | 'WEATHER_DIVERT' | 'TANKER_RTB_EARLY' | 'FLIGHT_REDUCE'
  description: string
  triggerTime: number
  affectedId: string
  applied: boolean
  rtbMinutes?: number  // for TANKER_RTB_EARLY — minutes until RTB (0 = immediate after current receiver)
  reduceBy?: number    // for FLIGHT_REDUCE — number of aircraft to remove from flight
}

export interface AARSimState {
  currentTime: number
  isRunning: boolean
  speed: 1 | 2 | 5 | 10
  tankers: AARTanker[]
  receivers: AARReceiver[]
  voiceLog: VoiceCommEntry[]
  contingencies: AARContingency[]
  phase: 'SETUP' | 'RUNNING' | 'PAUSED' | 'COMPLETE'
  missionAborted: boolean
}

// ===== Prediction =====
export interface PredictionResponse {
  asset_type: string
  predicted_capacity: number
  confidence_interval: [number, number]
  detected_tempo: string
  tempo_confidence: number
  threat_level: string
  threat_score: number
  model_used: string
  prediction_time_ms: number
}

// ===== Intel =====
export interface IntelJob {
  job_id: string
  status: 'pending' | 'running' | 'complete' | 'failed'
  asset_id: string
  report_type: string
  created_at: string
  progress: number
  error?: string
}

// ===== Audit Trail =====
export interface AuditEntry {
  id: string
  timestamp: string
  model: string
  asset: string
  action: string
  result: string
  threat_level: string
  confidence: number
}

// ===== Edge Status =====
export interface EdgeStatus {
  connectivity: 'FULL' | 'DEGRADED' | 'DENIED'
  ddil_level: number
  last_cloud_contact: string | null
  edge_predictions_count: number
  sync_pending: number
  models: Record<string, string>
}

// ===== Model Info =====
export interface ModelInfo {
  name: string
  type: string
  size_mb: number
  edge_capable: boolean
  description: string
}

export interface ModelInfoResponse {
  models: ModelInfo[]
  total_edge_size_mb: number
  ensemble_weights: Record<string, Record<string, number>>
  domain_variable_map: Record<string, Record<string, string>>
  edge_value_proposition: Record<string, string>
}

// ===== Logistics — Fuel =====
export interface FuelState {
  asset_id: string
  asset_type: string
  domain: string
  capacity_lbs: number
  current_lbs: number
  fuel_pct: number
  burn_rate_lbs_hr: number
  time_to_bingo_hrs: number
  bingo_fuel_lbs: number
  status: 'GREEN' | 'AMBER' | 'RED' | 'BLACK'
  sorties_remaining: number
  avg_sortie_fuel_lbs: number
}

export interface TheaterFuelSummary {
  total_capacity_lbs: number
  total_on_hand_lbs: number
  theater_fuel_pct: number
  assets_green: number
  assets_amber: number
  assets_red: number
  assets_black: number
  daily_burn_rate_lbs: number
  days_of_supply: number
  fuel_states: FuelState[]
}

// ===== Logistics — Ordnance =====
export type OrdnanceCategory = 'A2A' | 'PGM' | 'CRUISE' | 'GP' | 'SDB' | 'DEW'

export interface OrdnanceStockpile {
  munition_id: string
  munition_name: string
  short_name: string
  category: OrdnanceCategory
  weight_lbs: number
  unit_cost_k: number
  on_hand: number
  daily_expenditure_rate: number
  days_of_supply: number
  status: 'GREEN' | 'AMBER' | 'RED'
}

export interface AssetLoadout {
  asset_id: string
  asset_type: string
  domain: string
  current_load: { munition_id: string; munition_name: string; quantity: number; weight_lbs: number }[]
  max_capacity: Record<string, number>
  current_weight_lbs: number
  max_weight_lbs: number
  stations_used: number
  stations_total: number
}

export interface TheaterOrdnanceSummary {
  stockpile: OrdnanceStockpile[]
  total_munitions: number
  critical_shortfalls: number
  loadouts: AssetLoadout[]
}

export interface SustainabilityResult {
  tempo_label: string
  sorties_per_day: number
  fuel_days: number
  ordnance_days: number
  limiting_factor: 'FUEL' | 'ORDNANCE'
  limiting_item: string
}

// ===== Radio Chatter Monitor =====
export type RadioSeverity = 'CRITICAL' | 'WARNING' | 'INFO' | 'ROUTINE'

export interface RadioTranscript {
  id: string
  text: string
  timestamp: number
  severity: RadioSeverity
  tags: string[]
  confidence: number
  isFinal: boolean
}

export interface RadioAnalysis {
  summary: string
  callsigns: string[]
  statusChanges: string[]
  threats: string[]
}

// ===== Tab Navigation =====
export type TabId =
  | 'logistics'
  | 'force'
  | 'tempo'
  | 'readiness'
  | 'fusion'
  | 'crossdomain'
  | 'intel'
  | 'audit'
  | 'aar'
  | 'tactical'

export interface Tab {
  id: TabId
  label: string
  shortcut: string
  icon: string
}

export const TABS: Tab[] = [
  { id: 'logistics', label: 'Logistics', shortcut: '0', icon: '#' },
  { id: 'force', label: 'Force Composition', shortcut: '1', icon: '@' },
  { id: 'tempo', label: 'Tempo Alerts', shortcut: '2', icon: '!' },
  { id: 'readiness', label: 'Asset Readiness', shortcut: '3', icon: '$' },
  { id: 'fusion', label: 'Multi-Domain Fusion', shortcut: '4', icon: '~' },
  { id: 'crossdomain', label: 'Cross-Domain', shortcut: '5', icon: 'x' },
  { id: 'intel', label: 'Intel Assessment', shortcut: '6', icon: '=' },
  { id: 'audit', label: 'Audit Trail', shortcut: '7', icon: '?' },
  { id: 'aar', label: 'AAR Engine', shortcut: '8', icon: '<<' },
  { id: 'tactical', label: 'Real-Time Tactical', shortcut: '9', icon: '>' },
]
